import type { CSSProperties, ReactNode } from 'react';
import type {
  CourseCardProps,
  ArticleCardProps,
  EventCardProps,
  CategoryCardProps,
  PageHeaderProps,
  PillProps,
  EmptyStateProps,
} from './SystemPrimitives.types';
import { sbText } from './i18nCatalogue';

// =============================================================================
// SystemUI
// =============================================================================
//
// This file holds two kinds of things:
//
//   1. PURE LAYOUT HELPERS (PageContainer, cardStyle, muted) — utilities
//      with no visual identity. Themes don't override these; they edit
//      design tokens instead.
//
//   2. DEFAULT PRIMITIVES (CourseCard, ArticleCard, EventCard, etc.) —
//      concrete React components implementing the prop contracts in
//      SystemPrimitives.types.ts. These are the FALLBACKS used when the
//      active theme doesn't provide its own override.
//
// System pages should NOT import default primitives directly. They go
// through {@code resolveSystemPrimitive(themeId, kind)}, which returns
// the theme override OR the default in this file.
//
// Why both live here:
//   Defaults need somewhere to live, and "the file that already had the
//   helpers" is a natural home. Splitting defaults into separate files
//   per primitive would just churn imports without earning anything.
//   When defaults grow large, we'll split per-primitive files.

// ---- Pure layout helpers (NOT primitives) ----------------------------------

/** Standard system-page wrapper — max width, side padding, top padding.
 *  Theme tokens drive the values; the structure is fixed. */
export function PageContainer({ children, narrow }: { children: ReactNode; narrow?: boolean }) {
  return (
    <div
      style={{
        maxWidth: narrow ? 720 : 'var(--sb-max-width-1200, var(--app-max-width, 1200px))',
        width: '100%',
        margin: '0 auto',
        padding: 'var(--space-6, 24px) var(--space-6, 24px) var(--space-12, 48px)',
      }}
    >
      {children}
    </div>
  );
}

/** Reusable bordered-surface style for ad-hoc cards a system page builds
 *  inline. Themes change the underlying tokens; this object stays stable. */
export const cardStyle: CSSProperties = {
  background: 'var(--color-surface-card, #fff)',
  // Use the semantic border token, falling back through neutral-200
  // for older themes that don't define one. The neutral ramp's light/
  // dark orientation differs across themes (high N = dark in some,
  // light in others), so reaching directly at neutral-200 breaks dark
  // themes. The semantic token always reads correctly on the current
  // surface.
  border: '1px solid var(--color-border-subtle, var(--color-neutral-200, #e5e7eb))',
  borderRadius: 'var(--radius-md, 8px)',
};

/** Secondary-text style. Same story — token-driven, structure fixed. */
export const muted: CSSProperties = {
  color: 'var(--color-text-secondary, #5f6675)',
  fontSize: 'var(--font-size-sm, 14px)',
};

// ---- Inline helpers --------------------------------------------------------

/** Local mirror of {@code resizeImageUrl} from
 *  {@code @akademi/site-builder-blocks/src/_shared/resizeImageUrl.ts}.
 *  Inlined so the runtime stays free of a circular dep on blocks.
 *  Keep the two in sync if either evolves. */
function resizeImageUrlInline(imageUrl: string, size: string): string {
  if (imageUrl.includes('/static/')) return imageUrl;
  const applyResize = (pathname: string) => {
    const dotIndex = pathname.lastIndexOf('.');
    if (dotIndex < 0) return pathname;
    const filePath = pathname.slice(0, dotIndex);
    const extension = pathname.slice(dotIndex);
    if (filePath.endsWith(`_${size}`)) return pathname;
    return `${filePath}_${size}${extension}`;
  };
  try {
    const url = new URL(imageUrl);
    url.pathname = applyResize(url.pathname);
    return url.toString();
  } catch {
    const match = imageUrl.match(/^([^?#]*)(\?[^#]*)?(#.*)?$/);
    if (!match) return imageUrl;
    const [, pathname = '', search = '', hash = ''] = match;
    return `${applyResize(pathname)}${search}${hash}`;
  }
}

/** Local mirror of {@code CourseCardImage} from
 *  {@code @akademi/site-builder-blocks/src/cards/CourseCardImage.tsx}.
 *  Inlined so the runtime stays free of a circular dep on blocks.
 *  Keep the two in sync if either evolves. */
function CourseCardImageInline({ image, alt = '' }: { image?: string | ReactNode; alt?: string }) {
  if (image == null) return null;
  if (typeof image === 'string') {
    return (
      <img
        src={resizeImageUrlInline(image, '680x0')}
        alt={alt}
        loading="lazy"
        decoding="async"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
      />
    );
  }
  return <>{image}</>;
}

// ---- Default primitives ----------------------------------------------------
//
// Each implements its types-only contract from SystemPrimitives.types.ts.
// All read CSS variables for color, type, radius — so even WITHOUT a theme
// override, swapping the active theme retints these.

/** Default PageHeader — kicker line, title, subtitle, optional action. */
export function PageHeader({ kicker, title, subtitle, action }: PageHeaderProps) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        gap: 'var(--space-4, 16px)',
        flexWrap: 'wrap',
        marginBottom: 'var(--space-8, 32px)',
      }}
    >
      <div>
        {kicker && (
          <div
            style={{
              fontSize: 'var(--font-size-xs, 12px)',
              color: 'var(--color-brand-500, #3b5bdb)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 'var(--font-tracking-wider, 0.05em)',
              marginBottom: 8,
            }}
          >
            {kicker}
          </div>
        )}
        <h1
          style={{
            margin: 0,
            fontSize: 'var(--font-size-2xl, 32px)',
            lineHeight: 1.15,
            fontWeight: 700,
            fontFamily: 'var(--font-family-display, inherit)',
            color: 'var(--color-text-primary, #0f1218)',
          }}
        >
          {title}
        </h1>
        {subtitle && (
          <p
            style={{
              margin: 'var(--space-3, 12px) 0 0',
              color: 'var(--color-text-secondary, #5f6675)',
              maxWidth: 720,
            }}
          >
            {subtitle}
          </p>
        )}
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}

/** Default Pill — three tones, optional active state. */
export function Pill({ children, tone = 'neutral', active }: PillProps) {
  const tones: Record<string, CSSProperties> = {
    neutral: {
      background: 'var(--color-neutral-100, #eef0f4)',
      color: 'var(--color-text-secondary, #5f6675)',
    },
    brand: {
      background: 'var(--color-brand-50, #f0f4ff)',
      color: 'var(--color-brand-600, #2a45b8)',
    },
    success: { background: '#dcfce7', color: '#166534' },
  };
  const activeOverride: CSSProperties = active
    ? { background: 'var(--color-brand-500, #3b5bdb)', color: '#fff' }
    : {};
  return (
    <span
      style={{
        ...tones[tone],
        ...activeOverride,
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 10px',
        borderRadius: 'var(--radius-pill, 9999px)',
        fontSize: 'var(--font-size-xs, 12px)',
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: 'var(--font-tracking-wider, 0.04em)',
      }}
    >
      {children}
    </span>
  );
}

/** Default CourseCard — vertical tile (image up top, title + meta below). */
export function CourseCard({
  href,
  title,
  subtitle,
  image,
  rating,
  duration,
  progress,
  level,
}: CourseCardProps) {
  return (
    <a href={href} style={{ ...cardStyle, overflow: 'hidden', display: 'block', color: 'inherit' }}>
      <div
        style={{
          height: 140,
          background:
            'linear-gradient(135deg, var(--color-brand-50, #f0f4ff), var(--color-brand-100, #dbe6ff))',
          position: 'relative',
        }}
      >
        <CourseCardImageInline image={image} alt={title} />
        {typeof progress === 'number' && progress > 0 && (
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: 3,
              background: 'rgba(0,0,0,0.1)',
            }}
          >
            <div
              style={{
                width: `${progress}%`,
                height: '100%',
                background: 'var(--color-brand-500, #3b5bdb)',
              }}
            />
          </div>
        )}
      </div>
      <div style={{ padding: 'var(--space-4, 16px)' }}>
        <h3
          style={{
            margin: 0,
            fontSize: 'var(--font-size-md, 16px)',
            fontWeight: 600,
            lineHeight: 1.3,
            color: 'var(--color-text-primary, #0f1218)',
          }}
        >
          {title}
        </h3>
        {subtitle && <div style={{ ...muted, marginTop: 4 }}>{subtitle}</div>}
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-3, 12px)',
            marginTop: 'var(--space-3, 12px)',
            ...muted,
            fontSize: 'var(--font-size-xs, 12px)',
          }}
        >
          {duration && <span>{duration}</span>}
          {rating != null && <span>{`★ ${rating}`}</span>}
          {level && <span>{level}</span>}
        </div>
      </div>
    </a>
  );
}

/** Render a cover-seed prop. Three input shapes are accepted:
 *
 *   - ReactNode  -> render as-is (caller passed an <Image>, an <svg>, etc.)
 *   - URL string -> background-image
 *   - hex/color  -> linear-gradient seeded from the color
 *
 *  URL detection is intentionally narrow: starts with `/`, `http://`,
 *  `https://`, or `data:`. Anything else is treated as a CSS color seed.
 *  Themes are free to override any cover-using primitive entirely; this
 *  helper is for the default render path only. */
function renderCover(cover: string | ReactNode | undefined): ReactNode {
  if (cover == null) return null;
  if (typeof cover !== 'string') return cover;
  const isUrl = /^(\/|https?:\/\/|data:)/.test(cover);
  if (isUrl) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          backgroundImage: `url("${cover}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
    );
  }
  // Hex / named color / token reference -> gradient
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        background: `linear-gradient(135deg, ${cover}, ${cover}aa)`,
      }}
    />
  );
}

/** Default ArticleCard — vertical: cover hint up top, then category /
 *  title / excerpt / date+author meta. `featured` makes it wider with a
 *  taller image (used for the lead article on news index). */
export function ArticleCard({
  href,
  title,
  date,
  excerpt,
  author,
  category,
  readTime,
  cover,
  featured,
}: ArticleCardProps) {
  const coverNode = renderCover(cover);
  return (
    <a
      href={href}
      style={{
        ...cardStyle,
        overflow: 'hidden',
        display: 'block',
        color: 'inherit',
        gridColumn: featured ? '1 / -1' : 'auto',
      }}
    >
      <div
        style={{
          aspectRatio: featured ? '21 / 9' : '16 / 9',
          background: 'var(--color-neutral-100, #f4f4f5)',
          position: 'relative',
        }}
      >
        {coverNode}
      </div>
      <div style={{ padding: featured ? 'var(--space-6, 24px)' : 'var(--space-4, 16px)' }}>
        {category && (
          <div
            style={{
              fontSize: 'var(--font-size-xs, 12px)',
              color: 'var(--color-brand-600, #2a45b8)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 'var(--font-tracking-wider, 0.04em)',
              marginBottom: 6,
            }}
          >
            {category}
          </div>
        )}
        <h3
          style={{
            margin: 0,
            fontSize: featured ? 'var(--font-size-xl, 24px)' : 'var(--font-size-md, 16px)',
            fontWeight: 600,
            lineHeight: 1.25,
            fontFamily: 'var(--font-family-display, inherit)',
            color: 'var(--color-text-primary, #0f1218)',
          }}
        >
          {title}
        </h3>
        {excerpt && (
          <p
            style={{
              ...muted,
              margin: '8px 0 0',
              lineHeight: 1.45,
            }}
          >
            {excerpt}
          </p>
        )}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            marginTop: 'var(--space-3, 12px)',
            ...muted,
            fontSize: 'var(--font-size-xs, 12px)',
          }}
        >
          <span>{date}</span>
          {author && <span>{`· ${author}`}</span>}
          {readTime && <span>{`· ${readTime}`}</span>}
        </div>
      </div>
    </a>
  );
}

/** Default EventCard — horizontal: cover left, details middle, optional
 *  action slot on the right. The default keeps the primary link on the
 *  cover/title region; the action slot is independent (so an "Apply"
 *  button doesn't trigger navigation by accident). */
export function EventCard({
  href,
  title,
  type,
  startDate,
  endDate,
  duration,
  level,
  city,
  area,
  code,
  applyDeadline,
  status,
  cover,
  action,
}: EventCardProps) {
  const coverNode = renderCover(cover);
  const dateRange = endDate && endDate !== startDate ? `${startDate} - ${endDate}` : startDate;
  const isPast = status === 'past';
  return (
    <article
      style={{
        ...cardStyle,
        display: 'grid',
        gridTemplateColumns: action ? '160px 1fr auto' : '160px 1fr',
        gap: 20,
        alignItems: 'center',
        padding: 16,
        opacity: isPast ? 0.6 : 1,
      }}
    >
      <a href={href} style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}>
        <div style={{ height: 100, borderRadius: 8, overflow: 'hidden', position: 'relative' }}>
          {coverNode}
          {type && (
            <span
              style={{
                position: 'absolute',
                top: 8,
                left: 8,
                background: 'rgba(255,255,255,0.95)',
                color: 'var(--color-text-primary, #18181b)',
                fontSize: 10,
                padding: '2px 8px',
                borderRadius: 999,
                fontWeight: 600,
                letterSpacing: 'var(--font-tracking-wider, 0.04em)',
                textTransform: 'uppercase',
                fontFamily: 'var(--font-family-mono, ui-monospace, monospace)',
              }}
            >
              {type}
            </span>
          )}
        </div>
      </a>

      <a href={href} style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}>
        {(city || area) && (
          <div
            style={{
              fontSize: 11,
              fontFamily: 'var(--font-family-mono, ui-monospace, monospace)',
              letterSpacing: 'var(--font-tracking-wider, 0.04em)',
              textTransform: 'uppercase',
              color: 'var(--color-text-secondary, #71717a)',
            }}
          >
            {[city, area].filter(Boolean).join(' · ')}
          </div>
        )}
        <h3
          style={{
            margin: '4px 0',
            fontSize: 18,
            fontWeight: 600,
            fontFamily: 'var(--font-family-display, inherit)',
            color: 'var(--color-text-primary, #18181b)',
          }}
        >
          {title}
        </h3>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 16,
            marginTop: 8,
            fontSize: 12,
            color: 'var(--color-text-secondary, #71717a)',
          }}
        >
          <span>
            <strong style={{ color: 'var(--color-text-primary, #18181b)' }}>
              {sbText('systemUi.eventCard.dates')}
            </strong>{' '}
            {dateRange}
          </span>
          {duration && (
            <span>
              <strong style={{ color: 'var(--color-text-primary, #18181b)' }}>
                {sbText('systemUi.eventCard.duration')}
              </strong>{' '}
              {duration}
            </span>
          )}
          {level && (
            <span>
              <strong style={{ color: 'var(--color-text-primary, #18181b)' }}>
                {sbText('systemUi.eventCard.level')}
              </strong>{' '}
              {level}
            </span>
          )}
          {code && (
            <span style={{ fontFamily: 'var(--font-family-mono, ui-monospace, monospace)' }}>
              {code}
            </span>
          )}
          {applyDeadline && status === 'upcoming' && (
            <span style={{ color: 'var(--color-warning, #b45309)' }}>
              {sbText('systemUi.eventCard.applyBy', { date: applyDeadline })}
            </span>
          )}
        </div>
      </a>

      {action && <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>{action}</div>}
    </article>
  );
}

/** Default CategoryCard — square card with cover seed, title, count. */
export function CategoryCard({ href, title, courseCount, description, cover }: CategoryCardProps) {
  const coverNode = renderCover(cover);
  return (
    <a
      href={href}
      style={{
        ...cardStyle,
        overflow: 'hidden',
        display: 'block',
        color: 'inherit',
      }}
    >
      <div
        style={{
          aspectRatio: '4 / 3',
          background: 'var(--color-neutral-100, #f4f4f5)',
          position: 'relative',
        }}
      >
        {coverNode}
      </div>
      <div style={{ padding: 'var(--space-4, 16px)' }}>
        <h3
          style={{
            margin: 0,
            fontSize: 'var(--font-size-md, 16px)',
            fontWeight: 600,
            lineHeight: 1.3,
            color: 'var(--color-text-primary, #0f1218)',
          }}
        >
          {title}
        </h3>
        {description && (
          <p style={{ ...muted, margin: '4px 0 0', lineHeight: 1.4 }}>{description}</p>
        )}
        {typeof courseCount === 'number' && (
          <div
            style={{
              ...muted,
              marginTop: 'var(--space-3, 12px)',
              fontSize: 'var(--font-size-xs, 12px)',
            }}
          >
            {sbText('systemUi.categoryCard.courses', { count: courseCount })}
          </div>
        )}
      </div>
    </a>
  );
}

/** Default EmptyState — centered, dashed border, optional action. */
export function EmptyState({ title, subtitle, action }: EmptyStateProps) {
  return (
    <div
      style={{
        padding: 48,
        textAlign: 'center',
        background: 'var(--color-surface-card, #fff)',
        border: '1px dashed var(--color-neutral-300, #d4d4d8)',
        borderRadius: 12,
      }}
    >
      <div
        style={{
          margin: 0,
          fontSize: 'var(--font-size-lg, 18px)',
          fontWeight: 600,
          color: 'var(--color-text-primary, #18181b)',
        }}
      >
        {title}
      </div>
      {subtitle && (
        <p
          style={{
            ...muted,
            margin: '8px 0 0',
            maxWidth: 480,
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          {subtitle}
        </p>
      )}
      {action && <div style={{ marginTop: 'var(--space-4, 16px)' }}>{action}</div>}
    </div>
  );
}
